import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import { isInputRequestBlock } from '../../../data-contract/chat';
import type { ChatStream, ChatStreamListener, SendMessageParams } from '../../../fetchers/chat/chat-fetchers';
import { useApplicationFetchers } from '../../../fetchers/use-application-fetchers';
import { chatKeys } from '../../api/query-keys';

export type UseSendChatMessage = {
    readonly send: (params: SendMessageParams, listeners?: Partial<ChatStreamListener>) => ChatStream;
    readonly stop: () => void;
};

export const useSendChatMessage = (): UseSendChatMessage => {
    const { sendChatMessage } = useApplicationFetchers();
    const queryClient = useQueryClient();
    const streamsRef = useRef<Set<ChatStream>>(new Set());

    const stop = useCallback(() => {
        for (const stream of streamsRef.current) {
            stream.stop();
        }
        streamsRef.current.clear();
    }, []);

    const send = useCallback(
        (params: SendMessageParams, listeners?: Partial<ChatStreamListener>) => {
            // The messages key is nested under the thread key, so a non-exact invalidation of
            // either also refetches the whole message list. Mid-turn that replaces the streamed
            // pages with the server's, dropping every event it has not persisted yet, so the
            // message list is only reconciled once the turn has settled.
            const invalidateThread = (): void => {
                queryClient.invalidateQueries({ queryKey: chatKeys.thread(params.threadId), exact: true });
                queryClient.invalidateQueries({ queryKey: chatKeys.threads(), exact: true });
            };

            const invalidateThreadAndMessages = (): void => {
                invalidateThread();
                queryClient.invalidateQueries({ queryKey: chatKeys.messages(params.threadId) });
            };

            let stream: ChatStream;
            const wrapped: Partial<ChatStreamListener> = {
                chunk: listeners?.chunk,
                contextRefresh: listeners?.contextRefresh,
                messageStart: (event) => {
                    listeners?.messageStart?.(event);
                    invalidateThread();
                },
                structuredResponse: (event) => {
                    listeners?.structuredResponse?.(event);
                    // The backend flags "needs input" only on the REST thread payload; refetch it
                    // as soon as an input-requesting block streams in instead of waiting for the poll.
                    if (event.payload.blocks.some(isInputRequestBlock)) {
                        invalidateThread();
                    }
                },
                complete: (event) => {
                    streamsRef.current.delete(stream);
                    listeners?.complete?.(event);
                    invalidateThreadAndMessages();
                },
                error: (event) => {
                    streamsRef.current.delete(stream);
                    listeners?.error?.(event);
                    // A transport error is retryable and the registry owns the reconnect, so
                    // nothing is refreshed here: each disconnect would otherwise restart the
                    // thread and list retry cycles from scratch. Only a server-sent error
                    // ends the turn, and its thread state is worth reconciling now.
                    if (!(event instanceof Error)) {
                        invalidateThread();
                    }
                },
            };

            const sourceStream = sendChatMessage(params, wrapped);
            stream = {
                start: () => sourceStream.start(),
                stop: () => {
                    streamsRef.current.delete(stream);
                    sourceStream.stop();
                },
            };
            streamsRef.current.add(stream);
            stream.start();

            return stream;
        },
        [sendChatMessage, queryClient],
    );

    useEffect(() => {
        return () => {
            stop();
        };
    }, [stop]);

    return { send, stop };
};
